import { useAuth, useOrganization } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import { toUnitDetailsUnitType } from "@/lib/unit-details/unit-type";
import type { UnitType } from "@/types/database";

// unit_details is the shared, platform-wide home for unit identity (keyed by
// Clerk org id), written by the hub's admin-gated "Details" page and read by
// every app — see scoutingops-supabase's
// 20260715162937_website_admin_settings.sql. Site-builder now sources the
// four identity fields from here instead of its own org_profiles copy, so a
// change made in the hub and a change made in site-builder land in one place.
//
// This is intentionally a narrow view of the table: only the identity fields
// site-builder reads or writes. Meeting schedule, regional formatting, and the
// public calendar feed also live on unit_details but are owned by the hub /
// calendar and are not touched here.
export interface UnitDetailsIdentity {
  org_id: string;
  unit_type: UnitType;
  unit_number: string | null;
  council_name: string | null;
  district_name: string | null;
  chartered_org_name: string | null;
}

export interface UnitDetailsIdentityUpdate {
  unit_number?: string | null;
  council_name?: string | null;
  district_name?: string | null;
  chartered_org_name?: string | null;
}

export function useUnitDetails() {
  const { organization } = useOrganization();
  const { isLoaded, isSignedIn } = useAuth();
  const getClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const orgId = organization?.id ?? null;
  const enabled = Boolean(orgId) && isLoaded && isSignedIn;
  const queryKey = ["unit-details", orgId] as const;

  const query = useQuery({
    queryKey,
    enabled,
    queryFn: async (): Promise<UnitDetailsIdentity | null> => {
      if (!orgId) throw new Error("No active organization");
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("unit_details" as any)
        .select("org_id, unit_type, unit_number, council_name, district_name, chartered_org_name")
        .eq("org_id", orgId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as UnitDetailsIdentity | null) ?? null;
    },
  });

  // Update the existing row's identity fields, or create one when the unit has
  // no unit_details yet. Creation needs a valid unit_type (NOT NULL + 5-value
  // CHECK); `fallbackUnitType` supplies it from the caller's org profile. An
  // existing row's unit_type is never touched — it's the hub's to own.
  const save = useMutation({
    mutationFn: async (input: {
      updates: UnitDetailsIdentityUpdate;
      fallbackUnitType?: UnitType | null;
    }): Promise<UnitDetailsIdentity> => {
      if (!orgId) throw new Error("No active organization");
      const supabase = await getClient();
      const existing = query.data;

      if (existing) {
        const { data, error } = await supabase
          .from("unit_details" as any)
          .update(input.updates as any)
          .eq("org_id", orgId)
          .select("org_id, unit_type, unit_number, council_name, district_name, chartered_org_name")
          .single();
        if (error) throw error;
        return data as unknown as UnitDetailsIdentity;
      }

      const unitType = toUnitDetailsUnitType(input.fallbackUnitType);
      if (!unitType) {
        throw new Error("Set your unit type in Scouting Ops before saving unit details here.");
      }

      const { data, error } = await supabase
        .from("unit_details" as any)
        .insert({ org_id: orgId, unit_type: unitType, ...input.updates } as any)
        .select("org_id, unit_type, unit_number, council_name, district_name, chartered_org_name")
        .single();
      if (error) throw error;
      return data as unknown as UnitDetailsIdentity;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    unitDetails: query.data ?? null,
    loading: query.isLoading,
    saving: save.isPending,
    error: query.error instanceof Error ? query.error.message : save.error instanceof Error ? save.error.message : null,
    save: (updates: UnitDetailsIdentityUpdate, fallbackUnitType?: UnitType | null) =>
      save.mutateAsync({ updates, fallbackUnitType }),
    reload: () => query.refetch(),
  };
}
